import { useContext, useMemo } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { usePaymentMethodAvailability } from '@/context/PaymentMethodAvailabilityContext';
import { PaymentMethodEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import { CreditCardForm } from '../PaymentForms/CreditCardForm';
import { OtherPaymentForm } from '../PaymentForms/OtherPaymentForm';
import { PaymentMethodRenderer } from '../PaymentMethodRenderer/PaymentMethodRenderer';
import { TaxInfoPrompt } from './TaxInfoPrompt';
import { Disclaimer } from '../Common/Disclaimer';
import { IOFDisclaimer } from '../Common/IOFDisclaimer';
import { PayWithHeader } from '../Common/PayWithHeader';
import { shouldShowApmDisclaimer, shouldShowPayWithHeader } from '@/utils/conditionalRendering';
import { isTopBoxPaymentMethod } from '@/utils/paymentMethods';

interface PaymentMethodFormProps {
  blockedForZipCode: boolean;
  setBlockedForZipCode: (showInvalidZipMode: boolean) => void;
  setIsZipCodeValid: (isZipCodeValid: boolean) => void;
}

export const PaymentMethodForm = ({
  blockedForZipCode,
  setBlockedForZipCode,
  setIsZipCodeValid,
}: PaymentMethodFormProps) => {
  const { paymentMethodSelected, paymentMethodsAvailable, template } = useContext(CheckoutContext);
  const { isUnavailable } = usePaymentMethodAvailability();

  // CREDIT_CARD is rendered here (not via PaymentMethodRenderer) so the
  // disclaimer block stays visible at the top level. The renderer's CC entry
  // is `hideDisclaimer` because it's used inside the OTHER drawer, where
  // OtherPaymentForm already renders the disclaimer.
  const PaymentMethodFormComponent = useMemo(() => {
    if (paymentMethodSelected === PaymentMethodEnum.OTHER) {
      return <OtherPaymentForm />;
    }
    if (paymentMethodSelected === PaymentMethodEnum.CREDIT_CARD) {
      return <CreditCardForm />;
    }
    return <PaymentMethodRenderer paymentMethod={paymentMethodSelected as ExcludeOtherPaymentMethod} />;
  }, [paymentMethodSelected]);
  // The transition key must NOT include `otherMethodSelected`: keying on it
  // remounted this whole subtree (dropdown + every APM <img> row) on each APM
  // switch inside "Other", re-fetching all logos. For OTHER we keep the key
  // stable and let OtherPaymentForm animate only its inner form on switch.
  const transitionKey = blockedForZipCode ? 'tax-info' : paymentMethodSelected;

  const handleValidate = (isValid: boolean) => {
    setIsZipCodeValid(isValid);
  };

  // Whether the selected method is one of the top payment-method boxes on the
  // main screen (before the synthetic "Other" tile). These primary methods get
  // a "Pay with X" header + the security disclaimer.
  const isTopBox = isTopBoxPaymentMethod(paymentMethodSelected, paymentMethodsAvailable);
  const showPayWithHeader = shouldShowPayWithHeader(paymentMethodSelected, template.features, isTopBox, isUnavailable);
  const payWithLabel = paymentMethodsAvailable.find(
    (pm) => pm.paymentMethodName === paymentMethodSelected
  )?.paymentMethodDisplayName;

  return (
    <div data-testid="payment-method-form" className="flex flex-col gap-4">
      <div
        key={transitionKey}
        data-testid="payment-method-form-transition"
        // `flex flex-col gap-2` gives the APM disclaimer a consistent gap from
        // the wallet form below it (combined with the disclaimer's own `pb-2`
        // it matches the 16px section rhythm). When no disclaimer renders the
        // container has a single child, so the gap is a no-op.
        className="checkout-payment-form-transition flex flex-col gap-2"
      >
        {showPayWithHeader && payWithLabel && <PayWithHeader label={payWithLabel} />}
        {shouldShowApmDisclaimer(paymentMethodSelected, template.features, isTopBox, isUnavailable) && <Disclaimer />}
        {blockedForZipCode ? (
          <TaxInfoPrompt onBack={() => setBlockedForZipCode(false)} onValidate={handleValidate} />
        ) : (
          PaymentMethodFormComponent
        )}
      </div>
      {!blockedForZipCode && !isUnavailable && <IOFDisclaimer />}
    </div>
  );
};
